import {
  keepPreviousData,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import { toast } from 'sonner';

import type { ApiError } from '@/core/api';
import * as api from './api';
import type { NoteInput } from './types';

export const noteKeys = {
  all: ['notes'] as const,
  lists: () => [...noteKeys.all, 'list'] as const,
  list: (params: api.NoteListParams) => [...noteKeys.lists(), params] as const,
};

export function useNotes(params: api.NoteListParams = {}) {
  return useQuery({
    queryKey: noteKeys.list(params),
    queryFn: () => api.listNotes(params),
    placeholderData: keepPreviousData,
  });
}

export function useCreateNote() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: NoteInput) => api.createNote(input),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: noteKeys.lists() });
      toast.success('Note added');
    },
    onError: (err: ApiError) => toast.error(err.message),
  });
}

export function useUpdateNote() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, input }: { id: string | number; input: Partial<NoteInput> }) =>
      api.updateNote(id, input),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: noteKeys.lists() });
    },
    onError: (err: ApiError) => toast.error(err.message),
  });
}

export function useDeleteNote() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string | number) => api.deleteNote(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: noteKeys.lists() });
      toast.success('Note deleted');
    },
    onError: (err: ApiError) => toast.error(err.message),
  });
}
